import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  FormControlLabel,
  Switch,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Card,
  CardContent,
  Alert,
  Snackbar,
  CircularProgress,
  Backdrop,
  Chip
} from '@mui/material';

// Import Material-UI icons
import {
  Autorenew,
  Add,
  Close,
  Edit,
  Delete
} from '@mui/icons-material';

const AutoRenewalTab = (props) => {
  // Modal state
  const [openAutoRenewalModal, setOpenAutoRenewalModal] = useState(false);
  const [selectedAutoRenewalForEdit, setSelectedAutoRenewalForEdit] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [autoRenewalToDelete, setAutoRenewalToDelete] = useState(null);

  // State for notifications
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // Handle success states
  useEffect(() => {
    if (props.isSuccess) {
      setSnackbarMessage('Auto-renewal operation completed successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setOpenAutoRenewalModal(false);
      setSelectedAutoRenewalForEdit(null);
      
      // Reset success state after showing notification
      setTimeout(() => {
        props.resetAutoRenewalSuccess && props.resetAutoRenewalSuccess();
      }, 100);
    }
  }, [props.isSuccess]);

  // Handle error states
  useEffect(() => {
    if (props.isError) {
      setSnackbarMessage(`Error: ${props.isError.message || 'Something went wrong'}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      
      // Reset error state after showing notification
      setTimeout(() => {
        props.resetAutoRenewalError && props.resetAutoRenewalError();
      }, 100);
    }
  }, [props.isError]);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Auto-renewal state
  const [autoRenewalData, setAutoRenewalData] = useState({
    active: true,
    paid_in_advance: false,
    cardinality: "",
    granularity: "",
  });

  // Validation state
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Use auto-renewal data from props if available
  const autoRenewal = props.autoRenewal || props.renewal || null;
  const hasExistingRenewal = !!autoRenewal;

  // Validation function
  const validateForm = () => {
    const newErrors = {};

    // Cardinality validation
    if (!autoRenewalData.cardinality || autoRenewalData.cardinality === "") {
      newErrors.cardinality = "Renewal cardinality is required";
    } else {
      const cardinalityNumber = parseInt(autoRenewalData.cardinality);
      if (isNaN(cardinalityNumber)) {
        newErrors.cardinality = "Cardinality must be a valid number";
      } else if (cardinalityNumber <= 0) {
        newErrors.cardinality = "Cardinality must be greater than 0";
      } else if (cardinalityNumber > 999) {
        newErrors.cardinality = "Cardinality must be less than 1000";
      }
    }

    // Granularity validation
    if (!autoRenewalData.granularity) {
      newErrors.granularity = "Renewal granularity is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Helper function to get field error
  const getFieldError = (fieldName) => {
    return touched[fieldName] && errors[fieldName];
  };

  // Helper function to check if field has error
  const hasFieldError = (fieldName) => {
    return touched[fieldName] && !!errors[fieldName];
  };

  // Auto-renewal handlers
  const handleAutoRenewalInputChange = (field) => (event) => {
    let value = event.target.value;
    
    // Special handling for cardinality to ensure it's a valid number
    if (field === 'cardinality') {
      // Only allow positive integers
      value = value.replace(/[^0-9]/g, '');
      // Limit to reasonable range
      if (value && parseInt(value) > 999) {
        value = '999';
      }
    }
    
    setAutoRenewalData(prev => ({
      ...prev,
      [field]: value
    }));

    // Mark field as touched
    setTouched(prev => ({
      ...prev,
      [field]: true
    }));

    // Clear error for this field if it exists
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleAutoRenewalSwitchChange = (field) => (event) => {
    const checked = event.target.checked;
    setAutoRenewalData(prev => ({
      ...prev,
      [field]: checked
    }));
  };

  const handleAddAutoRenewal = () => {
    // Mark all fields as touched for validation
    const allFields = ['cardinality', 'granularity'];
    const newTouched = {};
    allFields.forEach(field => {
      newTouched[field] = true;
    });
    setTouched(newTouched);

    if (!validateForm()) {
      return;
    }

    const autoRenewalPayload = {
      active: autoRenewalData.active,
      paid_in_advance: autoRenewalData.paid_in_advance,
      renewal_cardinality: parseInt(autoRenewalData.cardinality, 10),
      renewal_granularity: autoRenewalData.granularity,
      renewal_guid: autoRenewalData.renewal_guid || null,
    };

    console.log("Auto-renewal payload:", autoRenewalPayload);

    // Validate that cardinality is a valid number before submitting
    if (isNaN(autoRenewalPayload.renewal_cardinality)) {
      console.error("Invalid cardinality value:", autoRenewalData.cardinality);
      return;
    }

    if (selectedAutoRenewalForEdit) {
      // Edit existing auto-renewal
      if (props.handleEditAutoRenewal) {
        console.log("Editing auto-renewal with guid:", selectedAutoRenewalForEdit.guid);
        props.handleEditAutoRenewal({ ...autoRenewalPayload, auto_renewal_guid: selectedAutoRenewalForEdit.guid });
      }
    } else {
      // Add new auto-renewal
      if (props.handleAddAutoRenewal) {
        console.log("Adding new auto-renewal");
        props.handleAddAutoRenewal(autoRenewalPayload);
      }
    }

    handleCloseAutoRenewalModal();
  };

  const handleDeleteAutoRenewal = (auto_renewal_guid) => {
    if (props.handleDeleteAutoRenewal) {
      console.log("Deleting auto-renewal:", auto_renewal_guid);
      props.handleDeleteAutoRenewal(auto_renewal_guid);
    }
  };

  const handleOpenAutoRenewalModal = (autoRenewal = null) => {
    if (autoRenewal) {
      // Edit mode
      setSelectedAutoRenewalForEdit(autoRenewal);
      setAutoRenewalData({
        active: autoRenewal.active || true,
        paid_in_advance: autoRenewal.paid_in_advance || false,
        cardinality: autoRenewal.renewal_cardinality ? autoRenewal.renewal_cardinality.toString() : "",
        granularity: autoRenewal.renewal_granularity || "",
        renewal_guid: autoRenewal.guid || null,
      });
    } else {
      // Add mode
      setSelectedAutoRenewalForEdit(null);
      setAutoRenewalData({
        active: true,
        paid_in_advance: false,
        cardinality: "",
        granularity: "",
      });
    }
    setErrors({});
    setTouched({});
    setOpenAutoRenewalModal(true);
  };

  const handleCloseAutoRenewalModal = () => {
    setOpenAutoRenewalModal(false);
    setSelectedAutoRenewalForEdit(null);
    setAutoRenewalData({
      active: true,
      paid_in_advance: false,
      cardinality: "",
      granularity: "",
    });
    setErrors({});
    setTouched({});
  };

  const handleDeleteClick = (autoRenewal) => {
    setAutoRenewalToDelete(autoRenewal);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (autoRenewalToDelete) {
      handleDeleteAutoRenewal(autoRenewalToDelete.guid);
    }
    setDeleteConfirmOpen(false);
    setAutoRenewalToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setAutoRenewalToDelete(null);
  };



  return (
    <Box sx={{ }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              Auto-Renewal Configuration
            </Typography>
           <Typography variant="body2" color="text.secondary">
            Define rules for automatic policy renewal based on specific criteria
          </Typography>
          </Box>
        </Box>
        {!hasExistingRenewal && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenAutoRenewalModal()}
            sx={{
              backgroundColor: '#fba92c',
              color: 'white',
              '&:hover': { backgroundColor: '#e0941f' },
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 500,
            }}
          >
            Add Auto-Renewal Rule
          </Button>
        )}
      </Box>

      {/* Auto-renewal Rules List */}
      {hasExistingRenewal ? (
        <Card 
          elevation={0}
          sx={{ 
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            '&:hover': { boxShadow: 2 }
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 500 }}>
                    Auto-Renewal Configuration
                  </Typography>
                  <Chip
                    label={autoRenewal.active ? 'Active' : 'Inactive'}
                    size="small"
                    sx={{
                      backgroundColor: autoRenewal.active ? '#e8f5e8' : '#ffebee',
                      color: autoRenewal.active ? '#2e7d32' : '#c62828',
                      fontWeight: 500,
                      borderRadius: 1
                    }}
                  />
                </Box>
                
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Cardinality
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {autoRenewal.renewal_cardinality || 'N/A'}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Granularity
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {autoRenewal.renewal_granularity || 'N/A'}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Paid in Advance
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {autoRenewal.paid_in_advance ? 'Yes' : 'No'}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Created At
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {autoRenewal.created_at ? new Date(autoRenewal.created_at).toLocaleDateString() : 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton 
                  size="small"
                  onClick={() => handleOpenAutoRenewalModal(autoRenewal)}
                    sx={{ color: "#6b7280" }}
                >
                  <Edit sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton 
                  size="small"
                  onClick={() => handleDeleteClick(autoRenewal)}
                   sx={{ color: "#ef4444" }}
                >
                  <Delete sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </Box>
          </CardContent>
        </Card>
      ) : (
          <Box
            sx={{
             border: "2px dashed #d1d5db",
             borderRadius: 2,
             p: 8,
             display: "flex",
             flexDirection: "column",
             alignItems: "center",
             justifyContent: "center",
             minHeight: 300,
            }}
            >
        <Box
          sx={{
          width: 48,
          height: 48,
          borderRadius: "50%",
          backgroundColor: "#f3f4f6",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 3,
           }}
                >
                 <Autorenew sx={{ fontSize: 24, color: "#6b7280" }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  No auto-renewal configuration set up yet
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: "center" }}>
                  Configure rules for automatic policy renewal based on specific criteria.<br />
                </Typography>
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={() => handleOpenAutoRenewalModal()}
            sx={{
              textTransform: "none",
              borderColor: "#d1d5db",
              color: "#374151",
              "&:hover": {
                borderColor: "#9ca3af",
                backgroundColor: "#f9fafb",
              },
              px: 3,
              py: 1,
            }}
          >
            Add Auto-Renewal Rule
          </Button>
        </Box>
      )}

      {/* Auto-renewal Modal */}
      <Dialog
        open={openAutoRenewalModal}
        onClose={handleCloseAutoRenewalModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {selectedAutoRenewalForEdit ? 'Edit Auto-Renewal Rule' : 'Add Auto-Renewal Rule'}
              </Typography>
            </Box>
            <IconButton onClick={handleCloseAutoRenewalModal} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 0 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Active Toggle */}
            <FormControlLabel
              control={
                <Switch
                  checked={autoRenewalData.active}
                  onChange={handleAutoRenewalSwitchChange('active')}
                  // sx={{
                  //   '& .MuiSwitch-switchBase.Mui-checked': {
                  //     color: '#173E4B',
                  //   },
                  //   '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  //     backgroundColor: '#173E4B',
                  //   },
                  // }}
                />
              }
              label={
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    Active
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Whether this auto-renewal rule is active
                  </Typography>
                </Box>
              }
            />

            {/* Paid in Advance Toggle */}
            <FormControlLabel
              control={
                <Switch
                  checked={autoRenewalData.paid_in_advance}
                  onChange={handleAutoRenewalSwitchChange('paid_in_advance')}
                  // sx={{
                  //   '& .MuiSwitch-switchBase.Mui-checked': {
                  //     color: '#173E4B',
                  //   },
                  //   '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  //     backgroundColor: '#173E4B',
                  //   },
                  // }}
                />
              }
              label={
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    Paid in Advance
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Consider policies paid in advance before the current lapse period
                  </Typography>
                </Box>
              }
            />

            {/* Cardinality */}
            <FormControl fullWidth error={hasFieldError('cardinality')}>
              <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1 }}>
                Renewal Cardinality *
              </Typography>
              <TextField
                placeholder="e.g. 1"
                value={autoRenewalData.cardinality}
                onChange={handleAutoRenewalInputChange('cardinality')}
                variant="outlined"
                type="number"
                inputProps={{ 
                  min: 1,
                  max: 999,
                  step: 1
                }}
                error={hasFieldError('cardinality')}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
              {hasFieldError('cardinality') && (
                <FormHelperText>{getFieldError('cardinality')}</FormHelperText>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Renewal frequency cardinality
              </Typography>
            </FormControl>

            {/* Granularity */}
            <FormControl fullWidth error={hasFieldError('granularity')}>
              <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1 }}>
                Renewal Granularity *
              </Typography>
              <Select
                value={autoRenewalData.granularity}
                onChange={handleAutoRenewalInputChange('granularity')}
                displayEmpty
                variant="outlined"
                error={hasFieldError('granularity')}
                sx={{
                  borderRadius: 2,
                }}
              >
                <MenuItem value="" disabled>
                  Select granularity
                </MenuItem>
                <MenuItem value="Day">Daily</MenuItem>
                <MenuItem value="Month">Monthly</MenuItem>
                <MenuItem value="Year">Yearly</MenuItem>
              </Select>
              {hasFieldError('granularity') && (
                <FormHelperText>{getFieldError('granularity')}</FormHelperText>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Renewal frequency granularity
              </Typography>
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleCloseAutoRenewalModal}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              color: '#666'
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddAutoRenewal}
            sx={{
              backgroundColor: '#fba92c',
              color: 'white',
              '&:hover': { backgroundColor: '#e0941f' },
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500,
              minWidth: 120
            }}
          >
            {selectedAutoRenewalForEdit ? 'Update' : 'Add'} Rule
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCancelDelete}
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ pb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 500, color: '#d32f2f' }}>
            Delete Auto-Renewal Rule
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this auto-renewal rule? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleCancelDelete}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              color: '#666'
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            sx={{
              backgroundColor: '#d32f2f',
              color: 'white',
              '&:hover': { backgroundColor: '#b71c1c' },
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={props.isLoading || false}
      >
        <CircularProgress color="inherit" />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Processing Auto-Renewal ...
        </Typography>
      </Backdrop>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbarSeverity}
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AutoRenewalTab;
